import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CalendarDays, CheckCircle2, Clock, Leaf, Check } from "lucide-react";

export default function DailyPlansTab() {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [clients, setClients] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", date: new Date().toISOString().slice(0, 10), time: "09:00", reminder: "15", techniqueId: null });
  const [selected, setSelected] = useState({});

  const load = useCallback(async () => {
    try {
      const [assigned, cl, ts] = await Promise.all([
        api.get("/coach/plans/assigned-progress").then((r) => r.data).catch(() => []),
        api.get("/coach/plan-clients").then((r) => r.data).catch(() => []),
        api.get("/breathing-techniques").then((r) => r.data).catch(() => []),
      ]);
      setRows(Array.isArray(assigned) ? assigned : []);
      setClients(Array.isArray(cl) ? cl : []);
      setTechs(Array.isArray(ts) ? ts : []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const save = async () => {
    if (!form.title.trim() || selectedIds.length === 0) { toast.error(t("pl.d_missing")); return; }
    setSaving(true);
    try {
      await api.post("/plans", {
        title: form.title.trim(),
        description: form.description.trim(),
        date: form.date,
        time: form.time,
        breathing_technique_id: form.techniqueId,
        reminder_minutes_before: parseInt(form.reminder) || 15,
        assigned_client_ids: selectedIds,
      });
      toast.success(t("pl.d_assigned", { n: selectedIds.length }));
      setOpen(false);
      setForm({ title: "", description: "", date: new Date().toISOString().slice(0, 10), time: "09:00", reminder: "15", techniqueId: null });
      setSelected({});
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="pl-daily-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t("pl.d_sub")}</p>
        <Button className="gap-1.5 rounded-full" onClick={() => setOpen(true)} data-testid="pl-daily-new"><Plus className="h-4 w-4" /> {t("pl.d_new")}</Button>
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><CalendarDays className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("pl.d_empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("pl.d_empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => (
            <div key={p.id} data-testid={`pl-daily-${p.id}`} className={`flex items-center gap-3 rounded-xl border border-border bg-card p-3 ${p.completed ? "opacity-70" : ""}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-foreground">{p.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{p.client_name || "—"} · {shortDate(p.date)}{p.time ? ` · ${p.time}` : ""}</p>
                {p.description && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.description}</p>}
              </div>
              {p.completed ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {t("pl.d_done")}</span>
              ) : (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700"><Clock className="h-3.5 w-3.5" /> {t("pl.d_pending")}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pl-daily-dialog">
          <DialogHeader>
            <DialogTitle>{t("pl.d_new")}</DialogTitle>
            <DialogDescription>{t("pl.d_sub")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t("pl.d_title_ph")} data-testid="pl-title" />
            <Textarea className="min-h-[70px]" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("pl.d_desc_ph")} data-testid="pl-desc" />
            <div className="grid grid-cols-3 gap-2">
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("pl.d_date")}</label><Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="pl-date" /></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("pl.d_time")}</label><Input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} data-testid="pl-time" /></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("pl.d_reminder")}</label><Input type="number" min="0" value={form.reminder} onChange={(e) => setForm((f) => ({ ...f, reminder: e.target.value }))} data-testid="pl-reminder" /></div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.d_technique")}</label>
              {techs.length === 0 ? <p className="text-sm text-muted-foreground">{t("common.none_found")}</p> : (
                <div className="flex flex-wrap gap-1.5">
                  {techs.map((tt) => (
                    <button key={tt.id} onClick={() => setForm((f) => ({ ...f, techniqueId: f.techniqueId === tt.id ? null : tt.id }))} data-testid={`pl-tech-${tt.id}`} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm ${form.techniqueId === tt.id ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                      <Leaf className="h-3 w-3" /> {tt.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.d_clients")}</label>
              {clients.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">{t("pl.d_no_clients")}</p>
              ) : (
                <div className="space-y-1.5">
                  {clients.map((c) => {
                    const on = !!selected[c.id];
                    return (
                      <button key={c.id} onClick={() => setSelected((p) => ({ ...p, [c.id]: !p[c.id] }))} data-testid={`pl-client-${c.id}`} className={`flex w-full items-center gap-2 rounded-xl border p-2.5 text-left ${on ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${on ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>{on && <Check className="h-3 w-3" />}</span>
                        <span className="text-sm text-foreground">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button className="rounded-full" onClick={save} disabled={saving || !form.title.trim() || selectedIds.length === 0} data-testid="pl-save">{saving ? t("common.saving") : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
